var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { IMALoader } from "./ima-loader";
import { AdEventType } from "../types";
import { createImaOverlay, initImaAdsManagerToViewport } from "./ima-overlay";
/**
 * IMA Rewarded Video Ads Implementation
 * Manages rewarded video ad playback using Google IMA SDK
 *
 * Reward Logic:
 * - Only grant reward if COMPLETE event fires
 * - SKIPPED or USER_CLOSE = no reward
 * - Backend validation via AD_REWARDED event
 */
export class IMARewardedVideo {
    constructor(eventEmitter) {
        this.eventEmitter = eventEmitter;
        this.adDisplayContainer = null;
        this.adsLoader = null;
        this.adsManager = null;
        this.videoElement = null;
        this.containerElement = null;
        this.adContainerElement = null;
        this.closeButton = null;
        this.confirmModal = null;
        /** Tear-down for the adsManager viewport-resize listeners. See ima-overlay.ts. */
        this.detachViewportResize = null;
        this.adCompleted = false;
        this.resolvePromise = null;
        this.rejectPromise = null;
    }
    /**
     * Show rewarded video ad
     * CRITICAL: This must be called from a user gesture (click, touch)
     */
    show(config, adTagUrl) {
        return __awaiter(this, void 0, void 0, function* () {
            // Ensure IMA SDK is loaded
            yield IMALoader.load();
            // Emit ad requested event
            yield this.eventEmitter.emit(AdEventType.AD_REQUESTED, {
                placement: config.placement,
                adType: "rewarded",
                rewardType: config.rewardType,
            });
            return new Promise((resolve, reject) => {
                this.resolvePromise = resolve;
                this.rejectPromise = reject;
                try {
                    // Create UI elements
                    this.createVideoContainer();
                    // Initialize IMA
                    this.initializeIMA();
                    // CRITICAL: Initialize AdDisplayContainer synchronously in user gesture
                    // This MUST happen before requestAds() or IMA will mark request as invalid
                    this.adDisplayContainer.initialize();
                    console.log("[GetJarAds] AdDisplayContainer initialized (user gesture)");
                    // Now request ads
                    this.requestAds(adTagUrl);
                    console.log(`[GetJarAds] Rewarded video requested for ${config.placement}`);
                }
                catch (error) {
                    this.cleanup();
                    reject(error);
                }
            });
        });
    }
    /**
     * Create the fullscreen ad container using the shared IMA overlay
     * builder (see `ima-overlay.ts`). The rewarded variant additionally
     * appends a close X — IMA doesn't render a post-completion close for
     * HTML5 rewarded, so if the VAST has an end-card,
     * `ALL_ADS_COMPLETED` would hang until the user dismissed it, leaving
     * them stuck with a reward they earned but can't claim.
     *
     * Click semantics on the X — keyed on `adCompleted`:
     *  - Pre-completion (linear video still playing): treat as user-close
     *    → no reward. Routes through `finishWithNoReward('closed')` to
     *    match the existing USER_CLOSE outcome.
     *  - Post-completion (COMPLETE already fired): reward is earned, just
     *    tear down → `finishWithReward()`.
     */
    createVideoContainer() {
        const overlay = createImaOverlay('rewarded');
        this.containerElement = overlay.containerElement;
        this.adContainerElement = overlay.adContainerElement;
        this.videoElement = overlay.videoElement;
        this.closeButton = document.createElement('button');
        this.closeButton.type = 'button';
        this.closeButton.setAttribute('aria-label', 'Close ad');
        this.closeButton.textContent = '×';
        // `max(12px, env(safe-area-inset-*))` keeps the X reachable on iOS
        // devices with a notch or home indicator — without this, in
        // landscape orientation the X can land directly under the camera
        // notch and become untappable.
        this.closeButton.style.cssText = `
      position: absolute;
      top: max(12px, env(safe-area-inset-top, 0px));
      right: max(12px, env(safe-area-inset-right, 0px));
      width: 36px;
      height: 36px;
      border: 0;
      border-radius: 50%;
      background: rgba(0, 0, 0, 0.6);
      color: #fff;
      font-size: 22px;
      line-height: 36px;
      padding: 0;
      cursor: pointer;
      z-index: 1000000;
    `;
        this.closeButton.addEventListener('click', () => this.onCloseClicked());
        // Close button sits ABOVE the ad container (sibling of it inside the
        // backdrop, not a child) so its z-index 1000000 isn't capped by the
        // ad container's stacking context.
        this.containerElement.appendChild(this.closeButton);
        document.body.appendChild(this.containerElement);
    }
    /**
     * Handle close-X click. Reward gating matches the IMA SKIPPED / COMPLETE
     * semantics already used elsewhere in this class.
     *
     * Pre-completion: pause IMA and show the "you'll lose your reward"
     * confirm dialog. The user must explicitly confirm to actually close —
     * this trades one accidental tap for two intentional taps, which the
     * rewarded UX is built around (mid-stream "did you mean it?" prompts
     * are the standard rewarded-ad-network pattern).
     *
     * Post-completion: COMPLETE already fired, reward is granted, so the X
     * is just a "close the post-roll / end-card" — no confirm needed.
     */
    onCloseClicked() {
        if (this.adCompleted) {
            console.log("[GetJarAds] Rewarded video closed via X (post-completion) — REWARD KEPT");
            this.finishWithReward();
            return;
        }
        // Already mid-confirm — a stray X tap (the X is hidden behind the
        // modal so this shouldn't fire, but guard against future layout
        // changes) is a no-op.
        if (this.confirmModal)
            return;
        console.log("[GetJarAds] Rewarded video close-X tapped pre-completion — showing confirm dialog");
        this.pauseAndShowConfirm();
    }
    /**
     * Pause IMA and mount the "lose your reward" confirm modal. The modal
     * sits at a z-index above the close X so a stray tap on the X is
     * caught by the modal's full-overlay surface, not the underlying IMA
     * iframe.
     *
     * `adsManager.pause()` is best-effort — IMA will throw if pause is
     * called in a state where pause isn't supported (e.g. between ad
     * pods). We log + swallow rather than fail the show, because the
     * confirm dialog still gives the user the right outcome either way.
     */
    pauseAndShowConfirm() {
        var _a, _b;
        try {
            (_a = this.adsManager) === null || _a === void 0 ? void 0 : _a.pause();
        }
        catch (err) {
            console.warn("[GetJarAds] adsManager.pause() threw — proceeding with confirm dialog anyway:", err);
        }
        const modal = document.createElement('div');
        modal.id = 'getjar-rewarded-confirm';
        modal.setAttribute('role', 'dialog');
        modal.setAttribute('aria-modal', 'true');
        modal.setAttribute('aria-labelledby', 'getjar-rewarded-confirm-title');
        // Safe-area-aware padding so the card never lands under a notch or
        // home indicator. `env(safe-area-inset-*)` returns 0 on devices
        // without insets, so on desktop the padding stays a clean 16px all
        // around; on iOS portrait/landscape it extends inward by the
        // device-reported inset.
        modal.style.cssText = `
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.72);
      z-index: 1000010;
      display: flex;
      align-items: center;
      justify-content: center;
      padding-top: max(16px, env(safe-area-inset-top, 0px));
      padding-right: max(16px, env(safe-area-inset-right, 0px));
      padding-bottom: max(16px, env(safe-area-inset-bottom, 0px));
      padding-left: max(16px, env(safe-area-inset-left, 0px));
    `;
        const card = document.createElement('div');
        card.style.cssText = `
      background: #fff;
      color: #1b2430;
      border-radius: 10px;
      padding: 20px 22px;
      max-width: 320px;
      width: 100%;
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.35);
      font: 15px/1.4 system-ui, sans-serif;
      text-align: center;
    `;
        const title = document.createElement('div');
        title.id = 'getjar-rewarded-confirm-title';
        title.textContent = 'Close the ad?';
        title.style.cssText = 'font-weight: 600; font-size: 17px; margin-bottom: 6px;';
        const body = document.createElement('div');
        body.textContent = "You'll lose your reward.";
        body.style.cssText = 'color: #52607a; margin-bottom: 18px;';
        const actions = document.createElement('div');
        actions.style.cssText = 'display: flex; gap: 10px; justify-content: center;';
        const resumeBtn = document.createElement('button');
        resumeBtn.type = 'button';
        resumeBtn.textContent = 'Resume';
        resumeBtn.style.cssText = `
      flex: 1;
      padding: 10px 14px;
      border: 0;
      border-radius: 8px;
      background: #26C281;
      color: #fff;
      font: 600 15px/1.2 system-ui, sans-serif;
      cursor: pointer;
    `;
        resumeBtn.addEventListener('click', () => this.onConfirmResume());
        const closeBtn = document.createElement('button');
        closeBtn.type = 'button';
        closeBtn.textContent = 'Close';
        closeBtn.style.cssText = `
      flex: 1;
      padding: 10px 14px;
      border: 1px solid #c7cfdd;
      border-radius: 8px;
      background: #fff;
      color: #1b2430;
      font: 600 15px/1.2 system-ui, sans-serif;
      cursor: pointer;
    `;
        closeBtn.addEventListener('click', () => this.onConfirmClose());
        actions.appendChild(resumeBtn);
        actions.appendChild(closeBtn);
        card.appendChild(title);
        card.appendChild(body);
        card.appendChild(actions);
        modal.appendChild(card);
        // Mount inside the fullscreen container so it's torn down with the
        // overlay and the absolute-positioning anchors against the right
        // box. Falls back to body if the container is unexpectedly missing.
        ((_b = this.containerElement) !== null && _b !== void 0 ? _b : document.body).appendChild(modal);
        this.confirmModal = modal;
        // Initial focus on Resume — most accidental X-taps want to keep the
        // reward. Native Tab order between the two buttons stays correct.
        try {
            resumeBtn.focus();
        }
        catch (_c) {
            // focus() can throw in unusual environments; ignore.
        }
    }
    /**
     * Resume path from the confirm modal — tear the modal down and resume
     * IMA playback. `adsManager.resume()` is best-effort for the same
     * reason as pause().
     */
    onConfirmResume() {
        var _a;
        console.log("[GetJarAds] Rewarded video close-confirm dismissed — resuming");
        this.dismissConfirmModal();
        try {
            (_a = this.adsManager) === null || _a === void 0 ? void 0 : _a.resume();
        }
        catch (err) {
            console.warn("[GetJarAds] adsManager.resume() threw:", err);
        }
    }
    /**
     * Confirm path — user chose to close mid-stream. Resolve with the
     * distinct `'closed_before_complete'` reason so the host adapter can
     * map it to a separate telemetry outcome from IMA USER_CLOSE.
     */
    onConfirmClose() {
        console.log("[GetJarAds] Rewarded video close-confirm accepted — NO REWARD");
        this.dismissConfirmModal();
        this.finishWithNoReward('closed_before_complete');
    }
    dismissConfirmModal() {
        if (this.confirmModal && this.confirmModal.parentNode) {
            this.confirmModal.parentNode.removeChild(this.confirmModal);
        }
        this.confirmModal = null;
    }
    /**
     * Initialize IMA SDK components
     */
    initializeIMA() {
        const ima = IMALoader.getSDK();
        // AdDisplayContainer target is the overlay div, NOT the fullscreen
        // backdrop — see createVideoContainer() for the layout rationale.
        this.adDisplayContainer = new ima.AdDisplayContainer(this.adContainerElement, this.videoElement);
        // Create AdsLoader
        this.adsLoader = new ima.AdsLoader(this.adDisplayContainer);
        // Listen for AdsManager loaded
        this.adsLoader.addEventListener(ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED, this.onAdsManagerLoaded.bind(this), false);
        // Listen for ad errors
        this.adsLoader.addEventListener(ima.AdErrorEvent.Type.AD_ERROR, this.onAdError.bind(this), false);
    }
    /**
     * Request ads from VAST URL
     */
    requestAds(adTagUrl) {
        const ima = IMALoader.getSDK();
        const adsRequest = new ima.AdsRequest();
        // CRITICAL: Ensure output=vast (IMA may try to use xml_vast4)
        const normalizedUrl = adTagUrl.replace("output=xml_vast4", "output=vast");
        adsRequest.adTagUrl = normalizedUrl;
        adsRequest.linearAdSlotWidth = window.innerWidth;
        adsRequest.linearAdSlotHeight = window.innerHeight;
        adsRequest.nonLinearAdSlotWidth = window.innerWidth;
        adsRequest.nonLinearAdSlotHeight = window.innerHeight;
        // CRITICAL: Tell IMA this is user-initiated autoplay (required for Chrome)
        adsRequest.setAdWillAutoPlay(true);
        adsRequest.setAdWillPlayMuted(false);
        this.adsLoader.requestAds(adsRequest);
    }
    /**
     * Handle AdsManager loaded event
     */
    onAdsManagerLoaded(adsManagerLoadedEvent) {
        const ima = IMALoader.getSDK();
        // Get AdsManager
        const adsRenderingSettings = new ima.AdsRenderingSettings();
        adsRenderingSettings.restoreCustomPlaybackStateOnAdBreakComplete = true;
        this.adsManager = adsManagerLoadedEvent.getAdsManager(this.videoElement, adsRenderingSettings);
        // Add event listeners
        this.adsManager.addEventListener(ima.AdEvent.Type.LOADED, this.onAdLoaded.bind(this));
        this.adsManager.addEventListener(ima.AdEvent.Type.STARTED, this.onAdStarted.bind(this));
        this.adsManager.addEventListener(ima.AdEvent.Type.COMPLETE, this.onAdComplete.bind(this));
        this.adsManager.addEventListener(ima.AdEvent.Type.SKIPPED, this.onAdSkipped.bind(this));
        this.adsManager.addEventListener(ima.AdEvent.Type.USER_CLOSE, this.onAdUserClose.bind(this));
        this.adsManager.addEventListener(ima.AdEvent.Type.ALL_ADS_COMPLETED, this.onAllAdsCompleted.bind(this));
        this.adsManager.addEventListener(ima.AdErrorEvent.Type.AD_ERROR, this.onAdError.bind(this));
        try {
            // AdDisplayContainer already initialized in show() (user gesture).
            // Wire the adsManager to the viewport — init + follow-up resize +
            // window resize/orientationchange listeners — via the shared
            // helper so this stays in sync with the interstitial renderer.
            this.detachViewportResize = initImaAdsManagerToViewport(this.adsManager, ima, this.adContainerElement, 'rewarded');
            // Start ad playback
            this.adsManager.start();
        }
        catch (error) {
            console.error("[GetJarAds] Failed to start AdsManager:", error);
            this.onAdError(error);
        }
    }
    /**
     * Handle ad loaded event
     */
    onAdLoaded() {
        return __awaiter(this, void 0, void 0, function* () {
            console.log("[GetJarAds] Rewarded video loaded");
            yield this.eventEmitter.emit(AdEventType.AD_LOADED, {
                adType: "rewarded",
            });
        });
    }
    /**
     * Handle ad started event
     */
    onAdStarted() {
        return __awaiter(this, void 0, void 0, function* () {
            console.log("[GetJarAds] Rewarded video started");
            this.adCompleted = false;
            yield this.eventEmitter.emit(AdEventType.AD_STARTED, {
                adType: "rewarded",
            });
        });
    }
    /**
     * Handle ad complete event (CRITICAL for reward)
     */
    onAdComplete() {
        return __awaiter(this, void 0, void 0, function* () {
            console.log("[GetJarAds] Rewarded video completed - REWARD GRANTED");
            this.adCompleted = true;
            yield this.eventEmitter.emit(AdEventType.AD_COMPLETED, {
                adType: "rewarded",
            });
        });
    }
    /**
     * Handle ad skipped event — IMA's built-in skip button (skippable ads only).
     * Rewarded ads ARE configured unskippable in the oRTB request, so this
     * should not fire in production, but we map it defensively.
     */
    onAdSkipped() {
        console.log("[GetJarAds] Rewarded video skipped - NO REWARD");
        this.finishWithNoReward('skipped');
    }
    /**
     * Handle user close event — IMA's own close UI (when present).
     */
    onAdUserClose() {
        console.log("[GetJarAds] Rewarded video closed by user - NO REWARD");
        this.finishWithNoReward('closed');
    }
    /**
     * Handle all ads completed event
     */
    onAllAdsCompleted() {
        return __awaiter(this, void 0, void 0, function* () {
            console.log("[GetJarAds] All ads completed");
            yield this.eventEmitter.emit(AdEventType.AD_CLOSED, {
                adType: "rewarded",
            });
            // Grant reward if ad completed
            if (this.adCompleted) {
                yield this.eventEmitter.emit(AdEventType.AD_REWARDED, {
                    adType: "rewarded",
                    granted: true,
                });
                this.finishWithReward();
            }
            else {
                // Reached here without COMPLETE — treat as a close (the linear stream
                // ended without a COMPLETE event, which is closer to "user closed" than
                // "user pressed skip"). The IMA SKIPPED handler routes that case
                // separately above.
                this.finishWithNoReward('closed');
            }
        });
    }
    /**
     * Handle ad error event
     */
    onAdError(adErrorEvent) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            const errorMessage = ((_c = (_b = (_a = adErrorEvent === null || adErrorEvent === void 0 ? void 0 : adErrorEvent.getError) === null || _a === void 0 ? void 0 : _a.call(adErrorEvent)) === null || _b === void 0 ? void 0 : _b.getMessage) === null || _c === void 0 ? void 0 : _c.call(_b)) || "Unknown error";
            console.error("[GetJarAds] Rewarded video error:", errorMessage);
            yield this.eventEmitter.emit(AdEventType.AD_ERROR, {
                adType: "rewarded",
                error: errorMessage,
            });
            this.cleanup();
            if (this.rejectPromise) {
                this.rejectPromise(new Error(errorMessage));
                this.rejectPromise = null;
            }
        });
    }
    /**
     * Finish with reward granted
     */
    finishWithReward() {
        this.cleanup();
        if (this.resolvePromise) {
            this.resolvePromise({ granted: true });
            this.resolvePromise = null;
        }
    }
    /**
     * Finish with no reward. The `reason` discriminator lets the host adapter
     * map skip (`'skipped'`) vs close (`'closed'`) into distinct bridge
     * outcomes — both still resolve the game's promise with `granted: false`,
     * the difference is purely the `reason` / `outcome` on `ad_result`.
     */
    finishWithNoReward(reason) {
        this.cleanup();
        if (this.resolvePromise) {
            this.resolvePromise({ granted: false, reason });
            this.resolvePromise = null;
        }
    }
    /**
     * Cleanup IMA resources
     */
    cleanup() {
        // Tear down the viewport-resize listeners BEFORE destroying
        // adsManager — a late resize event firing against a destroyed
        // adsManager would throw.
        if (this.detachViewportResize) {
            this.detachViewportResize();
            this.detachViewportResize = null;
        }
        // Destroy AdsManager
        if (this.adsManager) {
            try {
                this.adsManager.destroy();
            }
            catch (e) {
                console.warn("[GetJarAds] Error destroying AdsManager:", e);
            }
            this.adsManager = null;
        }
        // Destroy AdsLoader
        if (this.adsLoader) {
            try {
                this.adsLoader.destroy();
            }
            catch (e) {
                console.warn("[GetJarAds] Error destroying AdsLoader:", e);
            }
            this.adsLoader = null;
        }
        // Remove DOM elements
        if (this.containerElement && this.containerElement.parentNode) {
            this.containerElement.parentNode.removeChild(this.containerElement);
        }
        this.adDisplayContainer = null;
        this.videoElement = null;
        this.adContainerElement = null;
        this.closeButton = null;
        // The confirm modal is a child of containerElement and is removed
        // along with it; null the ref so we never act on a detached node.
        this.confirmModal = null;
        this.containerElement = null;
        this.adCompleted = false;
    }
    /**
     * Destroy player
     */
    destroy() {
        this.cleanup();
        this.resolvePromise = null;
        this.rejectPromise = null;
    }
}
